
// Form2.jsx (no auto-save)
import { useState, useRef, useEffect, useCallback } from "react";
import styles from "./Form2.module.scss";
import saveIcon from "../../../assets/images/save-icon.svg";
import { Getcanleaddata, getJobIndustries, PostRegister } from "../../../apiServices";
import { currentlyWorkingReasons, currentlyNotWorkingReasons } from "../../../utility";

// ── OPTIONS DATA ──────────────────────────────────────────────────────────────
const FRESHER_EXP_OPTIONS = ["Fresher", "Experienced"];
const CURRENT_STATUS_OPTIONS = ["Currently Working", "Currently Not Working"];
const JOB_TYPE_OPTIONS = ["On Roll", "Contract", "Temporary", "Apprenticeship"];
const WORK_HOURS_OPTIONS = ["8 Hours", "10 Hours", "12 Hours"];
const DEPARTMENT_OPTIONS = ["Engineering", "Marketing", "Sales", "HR", "Finance", "Operations", "Design", "Legal", "Other"];

function AutoSelect({ options, placeholder, value, onChange, isError, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const getOptionValue = (opt) => (typeof opt === 'object' && opt !== null) ? (opt.label || opt.value || opt) : opt;
  const getOptionLabel = (opt) => (typeof opt === 'object' && opt !== null) ? (opt.label || opt.value || opt) : opt;
  const filtered = options.filter((o) => getOptionLabel(o).toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setSearch(""); }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className={`${styles.autoSelectWrap}${isError ? " " + styles.err : ""}${disabled ? " " + styles.disabled : ""}`} ref={wrapRef}>
      <div className={styles.autoSelectTrigger} onClick={() => !disabled && setOpen(p => !p)}>
        <span className={value ? styles.selectedVal : styles.placeholderVal}>{value || placeholder}</span>
        <span className={styles.arrow}>{open ? "▴" : "▾"}</span>
      </div>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrap}>
            <input autoFocus className={styles.searchInput} placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} onClick={(e) => e.stopPropagation()} />
          </div>
          <ul className={styles.optionList}>
            {filtered.length > 0 ? (
              filtered.map((opt, i) => (
                <li key={i} className={`${styles.optionItem}${value === getOptionValue(opt) ? " " + styles.active : ""}`}
                  onClick={() => { onChange(getOptionValue(opt)); setOpen(false); setSearch(""); }}>
                  {getOptionLabel(opt)}
                </li>
              ))
            ) : <li className={styles.noOption}>No options found</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function TextInput({ name, placeholder, value, onChange, onBlur, isError, disabled, maxLength }) {
  return (
    <div className={`${styles.inputWrap}${isError ? " " + styles.inputError : ""}${disabled ? " " + styles.disabled : ""}`}>
      <input name={name} placeholder={placeholder} value={value || ""} onChange={onChange} onBlur={onBlur} disabled={disabled} maxLength={maxLength} />
    </div>
  );
}

function NumberInput({ name, placeholder, value, onChange, onBlur, isError, disabled }) {
  const handleChange = (e) => {
    let v = e.target.value.replace(/[^0-9]/g, "");
    if (v.length > 2) v = v.slice(0, 2);
    onChange({ target: { name, value: v } });
  };
  return (
    <div className={`${styles.inputWrap}${isError ? " " + styles.inputError : ""}${disabled ? " " + styles.disabled : ""}`}>
      <input name={name} type="text" placeholder={placeholder} value={value || ""} onChange={handleChange} onBlur={onBlur} disabled={disabled} maxLength={2} inputMode="numeric" />
    </div>
  );
}

function YearInput({ name, placeholder, value, onChange, onBlur, isError, disabled }) {
  const handleChange = (e) => {
    let v = e.target.value.replace(/[^0-9]/g, "");
    if (v.length > 4) v = v.slice(0, 4);
    onChange({ target: { name, value: v } });
  };
  return (
    <div className={`${styles.inputWrap}${isError ? " " + styles.inputError : ""}${disabled ? " " + styles.disabled : ""}`}>
      <input name={name} type="text" placeholder={placeholder} value={value || ""} onChange={handleChange} onBlur={onBlur} disabled={disabled} maxLength={4} inputMode="numeric" />
    </div>
  );
}

const initForm = {
  workExpStatus: "", totalExp: "", currentStatus: "", reasonForChange: "",
  currentCompanyName: "", previousCompanyName: "", jobType: "", workHours: "",
  industry: "", department: "", expFrom: "", expTo: "", location: "",
  takeHomeSalary: "", expectedSalary: ""
};

export default function Form2({ onClose, id, recordType, form1Data, onSubmit, onBack, registerValidation, onValidationChange }) {
  const [formData, setFormData] = useState(initForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [experienceBackup, setExperienceBackup] = useState({});
  const [industryOptions, setIndustryOptions] = useState([]);
  // --- FIX: stable refs ---
  const validateRef = useRef(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const prevIsValidRef = useRef(true);

  const isFresher = formData.workExpStatus === "Fresher";
  const isWorking = formData.currentStatus === "Currently Working";
  const isNotWorking = formData.currentStatus === "Currently Not Working";
  const showStatus = formData.workExpStatus === "Experienced";
  const showWorkExpCard = showStatus && formData.currentStatus;

  const isFieldEmpty = (value) => !String(value || "").trim();
  const isValidYear = (year) => {
    if (!year || year.length !== 4) return false;
    const y = parseInt(year);
    const current = new Date().getFullYear();
    return y >= 1900 && y <= current + 10;
  };
  
  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const response = await getJobIndustries();
      if (response?.statuscode === 200) {
        const industries = response.results.map((item) => item.industry);
        setIndustryOptions(industries);
      }
    } catch (error) {
      console.error("Error fetching industries:", error);
    }
  };
  
  const getReasonOptions = () => {
    if (formData.currentStatus === "Currently Working") return currentlyWorkingReasons;
    if (formData.currentStatus === "Currently Not Working") return currentlyNotWorkingReasons;
    return [];
  };
  
  const getReasonLabel = () => {
    if (formData.currentStatus === "Currently Working") {
      return <>Reason for Job Change <span className={styles.required}>*</span></>;
    }
    if (formData.currentStatus === "Currently Not Working") {
      return <>Reason for Unemployment <span className={styles.required}>*</span></>;
    }
    return <>Reason <span className={styles.required}>*</span></>;
  };

  useEffect(() => { if (id) fetchCandidateWorkData(id); }, [id, recordType]);

  const fetchCandidateWorkData = async (recordId) => {
  setLoading(true);
  try {
    const response = await Getcanleaddata(recordId, recordType);
    if (response?.code === 200) {
      const candidate = response.data;
      let currentStatusValue = "";
      let showWorkFields = false;

      // Default values
currentStatusValue = "";
showWorkFields = false;

// If candidate is fresher
if (
  candidate.candidate_type === "Fresher" ||
  candidate.candidate_type === "fresher"
) {
  currentStatusValue = "";
  showWorkFields = false;
}

// Experienced candidates
else if (
  candidate.currently_working === "Yes" ||
  candidate.currently_working === "yes" ||
  candidate.currently_working === 1
) {
  currentStatusValue = "Currently Working";
  showWorkFields = true;
}
else if (
  candidate.currently_working === "No" ||
  candidate.currently_working === "no" ||
  candidate.currently_working === 0
) {
  currentStatusValue = "Currently Not Working";
  showWorkFields = true;
}
else {
  currentStatusValue = "";
  showWorkFields = false;
}

      const hasExperience = candidate.exp_in_years > 0;
      const workExpStatus = hasExperience ? "Experienced" : "Fresher";

      setFormData({
        workExpStatus,
        totalExp: hasExperience ? candidate.exp_in_years.toString() : "",
        currentStatus: currentStatusValue, // Will be empty for both "No" and null
        reasonForChange: candidate.reason_for_jobchange || candidate.reason_for_unemployment || "",
        currentCompanyName: currentStatusValue === "Currently Working" ? (candidate.company_name || "") : "",
        previousCompanyName: (candidate.currently_working === "No" || candidate.currently_working === "no" || candidate.currently_working === 0) ? (candidate.company_name || "") : "",
        jobType: showWorkFields ? (candidate.job_type || "") : "",
        workHours: showWorkFields ? (candidate.job_work_hours || "") : "",
        industry: showWorkFields ? (candidate.industry || "") : "",
        department: showWorkFields ? (candidate.department || "") : "",
        expFrom: showWorkFields ? String(candidate.experience_from || "") : "",
        expTo: showWorkFields ? String(candidate.experience_to || "") : "",
        location: showWorkFields ? (candidate.company_location || "") : "",
        takeHomeSalary: showWorkFields ? String(candidate.take_home_salary || "") : "",
        expectedSalary: String(candidate.expected_salary || "")
      });
      setErrors({});
    }
  } catch (error) { 
    console.error("Error fetching candidate work data:", error);
  }
  finally { 
    setLoading(false); 
  }
};

  // validate reads from ref - no formData dep
  const validate = useCallback((silent = false) => {
    const fd = formDataRef.current;
    const errs = {};

    const _isFresher = fd.workExpStatus === "Fresher";
    const _isWorking = fd.currentStatus === "Currently Working";
    const _isNotWorking = fd.currentStatus === "Currently Not Working";
    const _showStatus = fd.workExpStatus === "Experienced";
    const _showWorkExpCard = _showStatus && fd.currentStatus;

    if (!fd.workExpStatus) errs.workExpStatus = "This field is required";

    if (fd.workExpStatus === "Experienced") {
      if (!fd.totalExp) errs.totalExp = "This field is required";
      else if (isNaN(parseInt(fd.totalExp)) || parseInt(fd.totalExp) < 0) errs.totalExp = "Enter a valid number";
      if (!fd.currentStatus) errs.currentStatus = "This field is required";

      if (fd.currentStatus) {
        if (!fd.reasonForChange) errs.reasonForChange = "This field is required";
        if (!fd.jobType) errs.jobType = "Job type is required";
        if (!fd.workHours) errs.workHours = "Work hours is required";
        if (!fd.industry) errs.industry = "Industry is required";
        if (isFieldEmpty(fd.expFrom)) errs.expFrom = "Experience from is required";
        else if (!isValidYear(fd.expFrom)) errs.expFrom = "Enter a valid 4-digit year";
        if (isFieldEmpty(fd.expTo)) errs.expTo = "Experience to is required";
        else if (!isValidYear(fd.expTo)) errs.expTo = "Enter a valid 4-digit year";
        if (isFieldEmpty(fd.location)) errs.location = "Location is required";
        if (_isWorking && isFieldEmpty(fd.currentCompanyName)) errs.currentCompanyName = "Current company name is required";
        if (_isNotWorking && isFieldEmpty(fd.previousCompanyName)) errs.previousCompanyName = "Previous company name is required";
        if (isFieldEmpty(fd.takeHomeSalary)) errs.takeHomeSalary = "Take home salary is required";
        if (isFieldEmpty(fd.expectedSalary)) errs.expectedSalary = "Expected salary is required";
      }
    }

    if (fd.workExpStatus === "Fresher") {
      if (isFieldEmpty(fd.expectedSalary)) errs.expectedSalary = "Expected salary is required";
    }

    if (!silent) setErrors(errs);
    return Object.keys(errs).length === 0;
  }, []); // no deps - reads from ref

  validateRef.current = validate;

  // Register validation ONCE
  useEffect(() => {
    if (registerValidation) {
      registerValidation((silent = false) => validateRef.current(silent));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buildApiData = () => {
    const fd = formDataRef.current;
    if (fd.workExpStatus === "Fresher") {
      return { candidate_type: "Fresher", expected_salary: fd.expectedSalary?.trim() || "", experienced: 0, currently_working: null  };
    }
    const apiData = {
      candidate_type: "Experienced", exp_in_years: fd.totalExp, department: fd.department || "",
      experience_from: fd.expFrom, experience_to: fd.expTo, job_type_mode: fd.jobType,
      job_work_hours: fd.workHours, industry: fd.industry, take_home_salary: fd.takeHomeSalary,
      expected_salary: fd.expectedSalary, company_location: fd.location, experienced: 1
    };
    if (fd.currentStatus === "Currently Working") {
      apiData.currently_working = "Yes";
      apiData.reason_for_jobchange = fd.reasonForChange;
      apiData.company_name = fd.currentCompanyName;
    } else if (fd.currentStatus === "Currently Not Working") {
      apiData.currently_working = "No";
      apiData.reason_for_unemployment = fd.reasonForChange;
      apiData.company_name = fd.previousCompanyName;
    }
    
    return apiData;
  };

  useEffect(() => {
    if (onValidationChange) {
      const isValid = Object.keys(errors).length === 0;
      if (prevIsValidRef.current !== isValid) {
        prevIsValidRef.current = isValid;
        onValidationChange();
      }
    }
  }, [errors, onValidationChange]);

  // const handleWorkExpStatusChange = useCallback((val) => {
  //   setTouched(prev => ({ ...prev, workExpStatus: true }));
  //   setErrors({});

  //   if (val === "Fresher") {
  //     setExperienceBackup(prev => ({ ...formDataRef.current }));
  //     setFormData(prev => ({
  //       ...prev, workExpStatus: val, totalExp: "", currentStatus: "", reasonForChange: "",
  //       currentCompanyName: "", previousCompanyName: "", jobType: "", workHours: "", industry: "",
  //       department: "", expFrom: "", expTo: "", location: "", takeHomeSalary: ""
  //     }));
  //   } else if (val === "Experienced") {
  //     setFormData(prev => ({ ...prev, workExpStatus: val, ...experienceBackup }));
  //   } else {
  //     setFormData(prev => ({ ...prev, workExpStatus: val }));
  //   }
  // }, [experienceBackup]);
const handleWorkExpStatusChange = useCallback((val) => {
  setTouched(prev => ({
    ...prev,
    workExpStatus: true
  }));

  setErrors({});

  setFormData(prev => {

    // Store current experienced data before clearing
    if (val === "Fresher") {
      setExperienceBackup({
        totalExp: prev.totalExp,
        currentStatus: prev.currentStatus,
        reasonForChange: prev.reasonForChange,
        currentCompanyName: prev.currentCompanyName,
        previousCompanyName: prev.previousCompanyName,
        jobType: prev.jobType,
        workHours: prev.workHours,
        industry: prev.industry,
        department: prev.department,
        expFrom: prev.expFrom,
        expTo: prev.expTo,
        location: prev.location,
        takeHomeSalary: prev.takeHomeSalary,
      });

      return {
        ...prev,
        workExpStatus: val,
        totalExp: "",
        currentStatus: "",
        reasonForChange: "",
        currentCompanyName: "",
        previousCompanyName: "",
        jobType: "",
        workHours: "",
        industry: "",
        department: "",
        expFrom: "",
        expTo: "",
        location: "",
        takeHomeSalary: ""
      };
    }

    // Restore backup when changing back
    if (val === "Experienced") {
      return {
        ...prev,
        workExpStatus: val,
        ...experienceBackup
      };
    }

    return {
      ...prev,
      workExpStatus: val
    };
  });

}, [experienceBackup]);

  const handleCurrentStatusChange = useCallback((val) => {
    setTouched(prev => ({ ...prev, currentStatus: true }));
    setFormData(prev => ({
      ...prev, currentStatus: val,
      reasonForChange: "", jobType: "", workHours: "", industry: "", department: "",
      expFrom: "", expTo: "", takeHomeSalary: "", location: "",
      ...(val === "Currently Working" ? { previousCompanyName: "" } : { currentCompanyName: "" })
    }));
    setErrors(prev => ({ ...prev, currentStatus: "", reasonForChange: "" }));
  }, []);

  const handleSelect = useCallback((field) => (val) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setFormData(prev => ({ ...prev, [field]: val }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  }, []);

  const handleInput = useCallback((e) => {
    let { name, value } = e.target;

    // Salary fields -> numbers only + max 5 digits
    if (name === "takeHomeSalary" || name === "expectedSalary") {
      value = value.replace(/[^0-9]/g, "");
      if (value.length > 5) {
        value = value.slice(0, 5);
      }
    }

    // Company Location -> text only
    if (name === "location") {
      value = value.replace(/[^A-Za-z\s]/g, "");
    }

    setTouched(prev => ({ ...prev, [name]: true }));
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  }, []);

  const handleSave = useCallback(async () => {
    const isValid = validateRef.current(false);
    if (!isValid) return false;
    setLoading(true);
    try {
      const apiData = buildApiData();
      const response = await PostRegister(id, 2, apiData, recordType);
      if (response?.code === 200) {
        if (onSubmit) onSubmit(apiData, false);
        return true;
      } else {
        setErrors({ submit: response?.message || "Failed to save work experience" });
        return false;
      }
    } catch (error) {
      console.error("Error saving form2:", error);
      setErrors({ submit: "Network error. Please try again." });
      return false;
    } finally { setLoading(false); }
  }, [id, recordType, onSubmit]);

  if (loading) {
    return (
      <div className={styles.formContainer}>
        <div className={styles.formContent}><div className={styles.formTitle}>Loading Work Experience Data...</div></div>
      </div>
    );
  }

  return (
    <div className={styles.formContainer}>
      <div className={styles.formContent}>
        <div className={styles.formTitle}>Work Experience Details</div>
        {errors.submit && <div className={styles.errorSummary}>{errors.submit}</div>}

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>Fresher or Experience <span className={styles.required}>*</span></label>
            <AutoSelect options={FRESHER_EXP_OPTIONS} placeholder="Select a option" value={formData.workExpStatus} onChange={handleWorkExpStatusChange} isError={!!errors.workExpStatus} />
            {errors.workExpStatus && <span className={styles.errorMsg}>{errors.workExpStatus}</span>}
          </div>

          {isFresher && (
            <div className={styles.formGroup}>
              <label>Expected Salary <span className={styles.required}>*</span></label>
              <TextInput name="expectedSalary" placeholder="e.g. ₹15,000" value={formData.expectedSalary} onChange={handleInput} isError={!!errors.expectedSalary} />
              {errors.expectedSalary && <span className={styles.errorMsg}>{errors.expectedSalary}</span>}
            </div>
          )}

          <div className={styles.formGroup}>
            <label>Total Years of Experience <span className={styles.required}>*</span></label>
            <NumberInput name="totalExp" placeholder="Enter total years (e.g., 5)" value={formData.totalExp} onChange={handleInput} isError={!!errors.totalExp} disabled={isFresher || !formData.workExpStatus} />
            {errors.totalExp && <span className={styles.errorMsg}>{errors.totalExp}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Current Status <span className={styles.required}>*</span></label>
            <AutoSelect options={CURRENT_STATUS_OPTIONS} placeholder="Select a option" value={formData.currentStatus} onChange={handleCurrentStatusChange} isError={!!errors.currentStatus} disabled={isFresher || !formData.workExpStatus} />
            {errors.currentStatus && <span className={styles.errorMsg}>{errors.currentStatus}</span>}
          </div>
        </div>

        {showWorkExpCard && (
          <div className={styles.workExpCard}>
            <div className={styles.cardTitle}>Work Experience</div>
            <div className={styles.cardContent}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{getReasonLabel()}</label>
                  <AutoSelect key={formData.currentStatus} options={getReasonOptions()} placeholder="Select reason" value={formData.reasonForChange} onChange={handleSelect("reasonForChange")} isError={!!errors.reasonForChange} />
                  {errors.reasonForChange && <span className={styles.errorMsg}>{errors.reasonForChange}</span>}
                </div>
                <div className={styles.formGroup}>
                  <label>{isWorking ? "Current Company Name" : "Previous Company Name"} <span className={styles.required}>*</span></label>
                  <TextInput
                    name={isWorking ? "currentCompanyName" : "previousCompanyName"}
                    placeholder={isWorking ? "Enter Current Company Name" : "Enter Previous Company Name"}
                    value={isWorking ? formData.currentCompanyName : formData.previousCompanyName}
                    onChange={handleInput}
                    isError={!!(isWorking ? errors.currentCompanyName : errors.previousCompanyName)}
                  />
                  {isWorking && errors.currentCompanyName && <span className={styles.errorMsg}>{errors.currentCompanyName}</span>}
                  {isNotWorking && errors.previousCompanyName && <span className={styles.errorMsg}>{errors.previousCompanyName}</span>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{isWorking ? "Current Job Type" : "Previous Job Type"} <span className={styles.required}>*</span></label>
                  <AutoSelect options={JOB_TYPE_OPTIONS} placeholder="Select Job Type" value={formData.jobType} onChange={handleSelect("jobType")} isError={!!errors.jobType} />
                  {errors.jobType && <span className={styles.errorMsg}>{errors.jobType}</span>}
                </div>
                <div className={styles.formGroup}>
                  <label>{isWorking ? "Current Work Hours" : "Previous Work Hours"} <span className={styles.required}>*</span></label>
                  <AutoSelect options={WORK_HOURS_OPTIONS} placeholder="Select Work Hours" value={formData.workHours} onChange={handleSelect("workHours")} isError={!!errors.workHours} />
                  {errors.workHours && <span className={styles.errorMsg}>{errors.workHours}</span>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{isWorking ? "Current Industry" : "Previous Industry"} <span className={styles.required}>*</span></label>
                  <AutoSelect options={industryOptions} placeholder="Select Industry" value={formData.industry} onChange={handleSelect("industry")} isError={!!errors.industry} />
                  {errors.industry && <span className={styles.errorMsg}>{errors.industry}</span>}
                </div>
                <div className={styles.formGroup}>
                  <label>Department (Optional)</label>
                  <AutoSelect options={DEPARTMENT_OPTIONS} placeholder="Select Department" value={formData.department} onChange={handleSelect("department")} isError={!!errors.department} />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Work Experience From <span className={styles.required}>*</span></label>
                  <YearInput name="expFrom" placeholder="Enter 4-digit year (e.g., 2020)" value={formData.expFrom} onChange={handleInput} isError={!!errors.expFrom} />
                  {errors.expFrom && <span className={styles.errorMsg}>{errors.expFrom}</span>}
                </div>
                <div className={styles.formGroup}>
                  <label>Work Experience To <span className={styles.required}>*</span></label>
                  <YearInput name="expTo" placeholder={isWorking ? "Present (or enter year)" : "Enter 4-digit year (e.g., 2023)"} value={formData.expTo} onChange={handleInput} isError={!!errors.expTo} />
                  {errors.expTo && <span className={styles.errorMsg}>{errors.expTo}</span>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Take Home Salary <span className={styles.required}>*</span></label>
                  <TextInput name="takeHomeSalary" placeholder="e.g. ₹25,000" value={formData.takeHomeSalary} onChange={handleInput} isError={!!errors.takeHomeSalary} />
                  {errors.takeHomeSalary && <span className={styles.errorMsg}>{errors.takeHomeSalary}</span>}
                </div>
                <div className={styles.formGroup}>
                  <label>Expected Salary <span className={styles.required}>*</span></label>
                  <TextInput name="expectedSalary" placeholder="e.g. ₹35,000" value={formData.expectedSalary} onChange={handleInput} isError={!!errors.expectedSalary} />
                  {errors.expectedSalary && <span className={styles.errorMsg}>{errors.expectedSalary}</span>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Company Location <span className={styles.required}>*</span></label>
                  <TextInput name="location" placeholder="Enter Location" value={formData.location} onChange={handleInput} isError={!!errors.location} />
                  {errors.location && <span className={styles.errorMsg}>{errors.location}</span>}
                </div>
              </div>
            </div>
          </div>
        )}
               {/* ── Add Another Work Experience Button and List ── */}
        {/* {showStatus && formData.currentStatus && (
          <>
            <div className={styles.previousWorkTitle}>Previous Work Experience</div>
            {expList.map((exp, idx) => (
              <details key={idx} className={styles.accordionSection} open={idx === 0}>
                <summary>
                  Work Experience {idx + 1}
                  {expList.length > 1 && (
                    <button 
                      className={styles.removeBtn} 
                      onClick={(e) => {
                        e.preventDefault();
                        handleRemoveExperience(idx);
                      }}
                    >
                      ✕
                    </button>
                  )}
                </summary>
                <div className={styles.accordionBody}>
                  Row 1: Company Name + Location
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Company Name *</label>
                      <div className={`${styles.inputWrap}${expErrors[idx]?.companyName ? " " + styles.inputError : ""}`}>
                        <input
                          placeholder="Enter Company Name"
                          value={exp.companyName}
                          onChange={handleExpInput(idx, "companyName")}
                          disabled={isFresher}
                        />
                      </div>
                      {expErrors[idx]?.companyName && <span className={styles.errorMsg}>{expErrors[idx].companyName}</span>}
                    </div>
                    <div className={styles.formGroup}>
                      <label>Location *</label>
                      <div className={`${styles.inputWrap}${expErrors[idx]?.location ? " " + styles.inputError : ""}`}>
                        <input
                          placeholder="Enter Location"
                          value={exp.location}
                          onChange={handleExpInput(idx, "location")}
                          disabled={isFresher}
                        />
                      </div>
                      {expErrors[idx]?.location && <span className={styles.errorMsg}>{expErrors[idx].location}</span>}
                    </div>
                  </div>

                  Row 2: Job Type + Department
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Job Type *</label>
                      <AutoSelect
                        options={JOB_TYPE_OPTIONS}
                        placeholder="Select Job Type"
                        value={exp.jobType}
                        onChange={handleExpSelect(idx, "jobType")}
                        isError={!!expErrors[idx]?.jobType}
                        disabled={isFresher}
                      />
                      {expErrors[idx]?.jobType && <span className={styles.errorMsg}>{expErrors[idx].jobType}</span>}
                    </div>
                    <div className={styles.formGroup}>
                      <label>Department *</label>
                      <AutoSelect
                        options={DEPARTMENT_OPTIONS}
                        placeholder="Select Department"
                        value={exp.department}
                        onChange={handleExpSelect(idx, "department")}
                        isError={!!expErrors[idx]?.department}
                        disabled={isFresher}
                      />
                      {expErrors[idx]?.department && <span className={styles.errorMsg}>{expErrors[idx].department}</span>}
                    </div>
                  </div>

                  Row 3: Work Experience From + Work Experience To
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Work Experience From *</label>
                      <div className={`${styles.inputWrap}${expErrors[idx]?.expFrom ? " " + styles.inputError : ""}`}>
                        <input
                          placeholder="e.g. Jan 2018"
                          value={exp.expFrom}
                          onChange={handleExpInput(idx, "expFrom")}
                          disabled={isFresher}
                        />
                      </div>
                      {expErrors[idx]?.expFrom && <span className={styles.errorMsg}>{expErrors[idx].expFrom}</span>}
                    </div>
                    <div className={styles.formGroup}>
                      <label>Work Experience To *</label>
                      <div className={`${styles.inputWrap}${expErrors[idx]?.expTo ? " " + styles.inputError : ""}`}>
                        <input
                          placeholder="e.g. Dec 2021"
                          value={exp.expTo}
                          onChange={handleExpInput(idx, "expTo")}
                          disabled={isFresher}
                        />
                      </div>
                      {expErrors[idx]?.expTo && <span className={styles.errorMsg}>{expErrors[idx].expTo}</span>}
                    </div>
                  </div>
                </div>
              </details>
            ))}

            Add Another Work Experience Button
            <div className={styles.addExpBtnWrapper}>
              <button className={styles.btnAdd} onClick={handleAddExperience} disabled={isFresher}>
                ＋ Add Another Work Experience
              </button>
            </div>
          </>
        )} */}
      </div>

      <div className={styles.buttonWrapper}>
        <div className={styles.btnGroupBetween}>
          <div></div>
          <div className={styles.btnGroupRight}>
            <button className={styles.btnCancel} onClick={onBack}>Back</button>
            <button className={styles.btnSave} onClick={handleSave}>
              <img src={saveIcon} alt="" width="16" height="16" />
              Save & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}